import { useEffect, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import { InAppNotificationsContext } from 'src/context/InAppNotificationsProvider/useInAppNotificationsContext';
import InAppNotification from 'src/features/inAppNotifications/presentation/components/InAppNotification';
import useInAppNotifications from 'src/features/inAppNotifications/presentation/hooks/useInAppNotifications';
import useTrackInAppNotificationEvents from 'src/features/inAppNotifications/presentation/hooks/useTrackInAppNotificationEvents';
import { InAppNotification as InAppNotificationInterface } from 'src/types/common';

import {
  getSeenNotifications,
  saveSeenNotification,
} from 'src/utils/localStorage/notifications';

type DismissNotificationFn = (
  reason?: 'dismiss_click' | 'timeout' | 'content_click'
) => void;

const hiddenRoutes = [
  '/cart',
  '/checkout',
  '/checkout/3ds-response',
  '/profile',
  '/profile-edit',
  '/delete-account',
  '/payment-cards-list',
  '/cards/new',
  '/3ds-redirect',
  '/registration',
  '/login',
  '/password-reset',
  '/address/new',
  '/addresslist',
];

const InAppNotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { trackNotificationClose, trackNotificationAutoClose } =
    useTrackInAppNotificationEvents();
  const { notifications } = useInAppNotifications();
  const [unseenNotification, setUnseenNotification] =
    useState<InAppNotificationInterface | null>(null);

  const { pathname } = useLocation();
  const matchNPS = useMatch('/nps/users/:user_uuid');
  const match3DSVerification = useMatch('/3ds-verification/:orderId');
  const matchEditAddress = useMatch('/address/edit/:addressPk');
  const matchPaymentVerification = useMatch('/payment-verification/*');

  const shouldHideNotifications =
    hiddenRoutes.includes(pathname) ||
    matchNPS ||
    match3DSVerification ||
    matchEditAddress ||
    matchPaymentVerification;

  useEffect(() => {
    if (!notifications) {
      setUnseenNotification(null);
      return;
    }

    const seen = getSeenNotifications();

    let next = notifications.find((notif) => !seen.includes(notif.pk));

    if (!next) {
      for (const seenPk of seen) {
        const candidate = notifications.find(
          (n) => n.pk === seenPk && n.is_persistent
        );
        if (candidate) {
          next = candidate;
          break;
        }
      }
    }

    setUnseenNotification(next ?? null);
  }, [notifications]);

  const dismissNotification: DismissNotificationFn = (
    reason = 'dismiss_click'
  ) => {
    if (!unseenNotification) return;

    saveSeenNotification(unseenNotification.pk);

    setUnseenNotification(null);

    const trackingActions = {
      dismiss_click: trackNotificationClose,
      timeout: trackNotificationAutoClose,
      content_click: null,
    };
    trackingActions[reason]?.(unseenNotification);
  };

  return (
    <InAppNotificationsContext.Provider
      value={{
        unseenNotification,
        dismissNotification,
        allNotifications: notifications,
      }}
    >
      <InAppNotification
        open={!!unseenNotification && !shouldHideNotifications}
        onClose={dismissNotification}
        notification={unseenNotification}
      />
      {children}
    </InAppNotificationsContext.Provider>
  );
};

export default InAppNotificationsProvider;
